import jwtDecode from 'jwt-decode';

/**
 * Identity roles of the OSK panel, as the login token carries them (claim `Roles`, the same one fas-admin
 * reads for the menu). "Osk" is the boss - every OSK owner has it.
 */
export enum OskStaffRole {
  Boss = 'Osk',
  Office = 'OskOffice',
  Instructor = 'OskInstructor',
  Lecturer = 'OskLecturer',
}

/**
 * The OSK role of the signed-in user, or null outside the OSK panel. It only shapes the UI - the API checks
 * the role itself on every request, also when the boss changed it after this token was issued.
 */
export function currentOskStaffRole(): OskStaffRole | null {
  const token = localStorage.getItem('authToken');
  if (!token) {
    return null;
  }
  try {
    const roles = jwtDecode<{ Roles?: string | string[] }>(token).Roles;
    const list = Array.isArray(roles) ? roles : (roles ?? '').split(';');
    return (Object.values(OskStaffRole) as string[]).find((role) => list.includes(role)) as OskStaffRole ?? null;
  } catch {
    return null;
  }
}

/** The boss: everything within the OSK, the only one who manages employees. */
export function isOskBoss(): boolean {
  return currentOskStaffRole() === OskStaffRole.Boss;
}

/** Adding students, editing their data and assigning access to them - the boss and the office. */
export function canAdministerStudents(): boolean {
  const role = currentOskStaffRole();
  return role === OskStaffRole.Boss || role === OskStaffRole.Office;
}

/** Running lectures on the exam stations - the boss, instructors and lecturers. */
export function canRunLectures(): boolean {
  const role = currentOskStaffRole();
  return role === OskStaffRole.Boss || role === OskStaffRole.Instructor || role === OskStaffRole.Lecturer;
}

/** The driving calendar: the boss, the office and the instructors (an instructor - their own slots and lessons). */
export function canUseDrivingCalendar(): boolean {
  const role = currentOskStaffRole();
  return role === OskStaffRole.Boss || role === OskStaffRole.Office || role === OskStaffRole.Instructor;
}

/** Slots and lessons of every instructor, warnings and fees - the boss and the office. */
export function canManageAllDrivingLessons(): boolean {
  return canAdministerStudents();
}

/** An instructor: the calendar is their own, "Mój dzień" on a phone. */
export function isOskInstructor(): boolean {
  return currentOskStaffRole() === OskStaffRole.Instructor;
}

/** A lecturer: in the calendar only the lectures. */
export function isOskLecturer(): boolean {
  return currentOskStaffRole() === OskStaffRole.Lecturer;
}
